import { createFileRoute, Navigate, useParams } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useEngagementQuery } from '@/lib/api/engagements';
import { reviewRedirectTarget } from '@/lib/engagement-modules';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { EmptyState } from '@/components/ui/empty-state';
import { AiConfidenceChip } from '@/components/ui/ai-confidence-chip';
import { CitationChip, type CitationChipData } from '@/components/ui/citation-chip';
import { CitationSourceModal } from '@/components/modals/citation-source-modal';
import { ReviewStateModal } from '@/components/modals/review-state-modal';
import { nextSelectionAfter } from '@/components/proposals/queue-selection';
import { ReasonTagSelect } from '@/components/proposals/reason-tag-select';
import { ProposedValueEditor } from '@/components/proposals/proposed-value-editor';
import { validateEditedValue } from '@/components/proposals/scoring-model';

import { useToast } from '@/components/ui/workspace';
import { useQueryClient } from '@tanstack/react-query';
import { useAiReviewPolicyQuery } from '@/lib/api/ai-review-policy';
import {
  decideProposal,
  useDecideProposalMutation,
  useProposalQueueQuery,
  useProposalSourceQuery,
  type DecideVerb,
  type ListProposalsFilters,
  type RoutingProposal,
  type SourceCitationDto,
} from '@/lib/api/review-proposals';
import { useOverrideTaxonomyQuery } from '@/lib/api/overrides';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/utils/cn';
import { titleCase } from '@/lib/utils/format';
import { stripMarkdownEscapes } from '@/lib/utils/ai-text';
import { deviationTypeLabel } from '@/lib/contract-labels';

/**
 * AI proposal review (T-0045 / E02-F05, layout revised 2026-07, clarity
 * pass 2026-10).
 *
 * Clarity-first revision replacing the scaffold queue. Design goals agreed
 * with the product owner (supersedes the original prototype screen, which
 * is outdated for this surface; chips/citations still use the load-bearing
 * E10 primitives per CLAUDE.md hard rule #3):
 *
 *   1. Every queue row leads with a HUMAN-READABLE HEADLINE derived from
 *      proposed_value — a reviewer must never need to open a row to know
 *      what it is. A row is one line (label · value, clamped) with the
 *      confidence at the right; a document every row in the group cites is
 *      named once in the group header, not on each row.
 *   2. The queue is GROUPED BY PROPOSAL TYPE with counts, so a baseline
 *      package reads like a table of contents (methodology → framework →
 *      rubrics → checkpoints → deliverables), not 100 identical cards. Two
 *      rows proposing the same thing (one contract fact, two values) flag
 *      the lower-confidence one.
 *   3. The detail pane serves ONE question, "is this claim true per the
 *      source?", in this order: the decision verbs in the header (on screen
 *      for every proposal, never below the evidence); the claim as a title
 *      plus readable text, never JSON; the evidence — one citation chip per
 *      place cited, the best passage with the quote highlighted, the rest
 *      behind a disclosure; then a footer with what approving does. Audit
 *      material (proposal id, date, raw JSON) lives behind "Proposal
 *      details".
 *   4. Verbs shown match what the server will ACCEPT: baseline-forming
 *      items never render a plain "Approved" button (the API blocks it —
 *      D-22 no-auto-accept); "Approve as Noted" is the primary verb there
 *      and the policy is explained inline instead of as a 409 error.
 *   5. Revise & Resubmit takes a note (the reason the AI re-reads with),
 *      confirms with a toast, and keeps the card on screen as "Revised,
 *      re-reading…" until a fresh proposal of that type lands — the card
 *      used to vanish silently because the default filter hides decided rows.
 */
/** `?proposal=<ai_proposal_id>`: open the queue on that proposal (T-0239, the document page links here). */
interface ProposalsSearch {
  proposal?: string;
}

export const Route = createFileRoute('/engagements/$engagementId/proposals')({
  validateSearch: (search: Record<string, unknown>): ProposalsSearch =>
    typeof search.proposal === 'string' && search.proposal.length > 0
      ? { proposal: search.proposal }
      : {},
  component: ProposalsRoute,
});

/**
 * The queue is the IV&V review surface (baseline items, checkpoints,
 * deliverables, contract facts). An engagement with no IV&V module has no
 * such queue: Discovery reviews its evidence on Findings and Selection its
 * suggestions on Requirements, so the route lands there instead, with a
 * one-line notice (`?from=proposals`). Links to this route (the Overview's
 * AI tile, the activity strip, the documents/review alias, bookmarks) keep
 * working for every engagement. `replace`, so Back does not bounce here.
 */
export function ProposalsRoute() {
  const { proposal } = Route.useSearch();
  const { engagementId } = useParams({ from: '/engagements/$engagementId/proposals' });
  const engagement = useEngagementQuery(engagementId);
  const target = engagement.data ? reviewRedirectTarget(engagement.data.modules_enabled) : null;

  if (engagement.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-ink-500">
        <Spinner /> Loading engagement…
      </div>
    );
  }
  if (target === 'discovery') {
    return (
      <Navigate
        to="/engagements/$engagementId/discovery/findings"
        params={{ engagementId }}
        search={{ area: 'all', status: 'proposed', from: 'proposals' }}
        replace
      />
    );
  }
  if (target === 'selection') {
    return (
      <Navigate
        to="/engagements/$engagementId/selection"
        params={{ engagementId }}
        search={{ from: 'proposals' }}
        replace
      />
    );
  }
  return <ProposalsTab focusProposalId={proposal} />;
}

// ===========================================================================
// Proposal-type presentation metadata
// ===========================================================================

interface TypeMeta {
  /** Short chip label shown on rows + detail header. */
  label: string;
  /** Queue group heading. */
  group: string;
  /** One-line group explainer under the heading. */
  groupHint: string;
  /** Sort position of the group in the queue. */
  order: number;
}

const TYPE_META: Record<string, TypeMeta> = {
  // T-0219: the three stage-2 types are suggestions against the firm's
  // assessment defaults; approving one applies it, nothing waits on them.
  methodology: {
    label: 'Assessment suggestion',
    group: 'Assessment suggestions · scoring model',
    groupHint:
      'The AI reading of the SOW suggests domains, criteria and weights; approving applies them to this engagement.',
    order: 0,
  },
  register_framework: {
    label: 'Assessment suggestion',
    group: 'Assessment suggestions · register framework',
    groupHint: 'The register scheme (e.g. RAID) the documents describe; approving binds it.',
    order: 1,
  },
  rubric: {
    label: 'Assessment suggestion',
    group: 'Assessment suggestions · domain rubrics',
    groupHint: 'Per-domain 0–5 scoring definitions; approving adds them to this engagement.',
    order: 2,
  },
  checkpoint: {
    label: 'Checkpoint',
    group: 'IV&V checkpoints',
    groupHint: 'Scheduled verification checkpoints for this engagement.',
    order: 3,
  },
  edl_item: {
    label: 'Deliverable',
    group: 'Expected deliverables',
    groupHint: 'Deliverables extracted from the contract documents (EDL).',
    order: 4,
  },
  domain_rating: {
    label: 'Posture rating',
    group: 'Domain posture ratings',
    groupHint: 'AI-read posture per health domain, extracted from status documents.',
    order: 5,
  },
  budget_entry: {
    label: 'Budget entry',
    group: 'Budget entries',
    groupHint: 'Spend / change-order figures extracted from status documents.',
    order: 6,
  },
  milestone_update: {
    label: 'Milestone',
    group: 'Milestone updates',
    groupHint: 'Schedule dates extracted from status documents.',
    order: 7,
  },
  finding: {
    label: 'Finding',
    group: 'Findings',
    groupHint: 'Risks, issues, and blockers the document raises.',
    order: 8,
  },
  register_update: {
    label: 'Register update',
    group: 'Register updates',
    groupHint: 'AI-routed changes to existing register entries.',
    order: 9,
  },
  new_record: {
    label: 'New record',
    group: 'New register records',
    groupHint: 'AI-proposed new register entries from routed documents.',
    order: 10,
  },
  change_order: {
    label: 'Change order',
    group: 'Change orders',
    groupHint:
      'Uploaded change orders, read by AI: what each one adds, modifies or removes in the contract.',
    order: 11,
  },
  deviation: {
    label: 'Deviation',
    group: 'Contract deviations',
    groupHint: 'Places where a status or test report shows the contract is not being met.',
    order: 12,
  },
  deliverable_submission: {
    label: 'Submission',
    group: 'Deliverable submissions',
    groupHint:
      'Uploaded documents the AI matched to an awaited deliverable, read against its acceptance criteria.',
    order: 9,
  },
  checkpoint_link: {
    label: 'Checkpoint links',
    group: 'Checkpoint dependencies',
    groupHint: 'Which contracted deliverables each IV&V checkpoint is assessed against.',
    order: 7,
  },
  contract_fact: {
    label: 'Contract fact',
    group: 'Contract facts',
    groupHint: 'Contract-level terms read from the contract: vendor, value, dates and protections.',
    order: 8,
  },
};

const VERDICT_LABELS: Record<string, string> = {
  met: 'Met',
  partially_met: 'Partially met',
  not_met: 'Not met',
  not_evidenced: 'Not shown in the document',
};

/** Short label for a proposal type ("Deliverable", "Change order", ...). */
export function proposalTypeLabel(type: string): string {
  return typeMeta(type).label;
}

function typeMeta(type: string): TypeMeta {
  return (
    TYPE_META[type] ?? {
      label: titleCase(type),
      group: titleCase(type),
      groupHint: '',
      order: 99,
    }
  );
}

/**
 * Row ids in the order the queue shows them: grouped by type in TYPE_META
 * order, then by confidence, highest first. The same procedure as the
 * queue's `groups`, so "the next row" means the row the reviewer sees next.
 */
function queueDisplayOrder(list: ReadonlyArray<RoutingProposal>): string[] {
  const byType = new Map<string, RoutingProposal[]>();
  for (const p of list) {
    const group = byType.get(p.proposal_type) ?? [];
    group.push(p);
    byType.set(p.proposal_type, group);
  }
  return [...byType.entries()]
    .sort((a, b) => typeMeta(a[0]).order - typeMeta(b[0]).order)
    .flatMap(([, group]) => [...group].sort((a, b) => b.confidence - a.confidence))
    .map((p) => p.id);
}

/** A value written as an identifier: lower-case words joined by underscores ("not_met", "high"). */
const IDENTIFIER_RE = /^[a-z0-9_]+$/;

/**
 * An identifier-like value as readable words ("not_met" → "Not Met"); any
 * other string as the AI wrote it, minus Markdown escapes (C2-33). Title
 * casing used to run over every string and split on hyphens, so a scale of
 * "0-5" read "0 5" and "Data Migration" was re-cased.
 */
export function displayProposalValue(value: string): string {
  const trimmed = value.trim();
  return IDENTIFIER_RE.test(trimmed) ? titleCase(trimmed) : stripMarkdownEscapes(trimmed);
}

const humanize = displayProposalValue;

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/** The first non-empty string among `keys`, without the parser's Markdown escapes (C2-33). */
function firstString(v: Record<string, unknown>, keys: string[]): string | null {
  for (const k of keys) {
    const raw = v[k];
    if (typeof raw === 'string' && raw.trim().length > 0) return stripMarkdownEscapes(raw.trim());
  }
  return null;
}

/**
 * The one-line answer to "what is this?". Never returns JSON — falls back
 * to the type label so a malformed payload still renders something sane.
 */
export function proposalHeadline(p: RoutingProposal): string {
  const v = asRecord(p.proposed_value);
  switch (p.proposal_type) {
    case 'edl_item':
      return (
        firstString(v, ['name', 'title', 'deliverable', 'description']) ?? 'Expected deliverable'
      );
    case 'rubric': {
      const domain = firstString(v, ['domain', 'domain_name', 'name']);
      return domain ? `Scoring rubric — ${humanize(domain)}` : 'Scoring rubric';
    }
    case 'checkpoint': {
      const name = firstString(v, ['name', 'title', 'checkpoint', 'label']);
      if (name) return name;
      const timing = firstString(v, ['timing', 'schedule', 'cadence']);
      return timing ? `IV&V checkpoint — ${humanize(timing)}` : 'IV&V checkpoint';
    }
    case 'methodology':
      return 'Engagement scoring methodology';
    case 'register_framework': {
      const scheme = firstString(v, ['scheme', 'framework', 'name', 'register_scheme']);
      return scheme ? `Register framework — ${humanize(scheme)}` : 'Register framework';
    }
    case 'domain_rating': {
      const r = firstString(v, ['rating']);
      return r ? `Domain posture — ${humanize(r)}` : 'Domain posture rating';
    }
    case 'budget_entry': {
      const amt = typeof v.amount === 'number' ? `$${v.amount.toLocaleString()}` : null;
      const layer = firstString(v, ['layer']);
      return [amt, layer ? humanize(layer) : null].filter(Boolean).join(' — ') || 'Budget entry';
    }
    case 'milestone_update':
      return firstString(v, ['name']) ?? 'Milestone date update';
    case 'finding':
      return firstString(v, ['title']) ?? 'Finding';
    case 'register_update':
      return firstString(v, ['summary', 'title', 'name']) ?? 'Update to an existing register entry';
    case 'new_record':
      return firstString(v, ['title', 'name', 'summary']) ?? 'New register entry';
    case 'change_order':
      return firstString(v, ['document_title']) ?? 'Change order';
    case 'deviation':
      return firstString(v, ['title']) ?? 'Contract deviation';
    case 'checkpoint_link':
      return `${firstString(v, ['checkpoint_name']) ?? 'Checkpoint'} depends on ${
        Array.isArray(v.deliverables) ? v.deliverables.length : 0
      } deliverable${Array.isArray(v.deliverables) && v.deliverables.length === 1 ? '' : 's'}`;
    case 'contract_fact': {
      const label = firstString(v, ['label']) ?? 'Contract fact';
      const value = firstString(v, ['value']);
      return value ? `${label}: ${value}` : label;
    }
    case 'deliverable_submission': {
      const name = firstString(v, ['deliverable_name']) ?? 'a deliverable';
      return `${firstString(v, ['document_title']) ?? 'Uploaded document'} → ${name}`;
    }
    default:
      return firstString(v, ['name', 'title', 'summary']) ?? typeMeta(p.proposal_type).label;
  }
}

const EDL_BUCKET_LABELS: Record<string, string> = {
  deliverable: 'Deliverables',
  milestone: 'Milestones',
  payment_term: 'Payment terms',
  kpi: 'KPIs',
  sla: 'SLAs',
  integration: 'Integrations',
};

/** The Contract-baseline section an EDL item will land in when approved. */
function edlBucketLabel(p: RoutingProposal): string {
  const t = firstString(asRecord(p.proposed_value), ['item_type']);
  return (t && EDL_BUCKET_LABELS[t]) ?? 'Deliverables';
}

/**
 * Re-baseline (C2-52): the item is already on the baseline. The API checks
 * the live register on every read (`existing_match` on the row); the
 * projector's annotation on the proposed value is the fallback for a queue
 * loaded from an older API.
 */
export function existingMatch(
  p: RoutingProposal,
): { name: string; dateChange: { current: string | null; proposed: string } | null } | null {
  const m = p.existing_match
    ? (p.existing_match as Record<string, unknown>)
    : asRecord(asRecord(p.proposed_value).existing_match);
  if (typeof m.name !== 'string') return null;
  const d = asRecord(asRecord(m.differences).expected_delivery_date);
  return {
    name: m.name,
    dateChange:
      typeof d.proposed === 'string'
        ? { current: typeof d.current === 'string' ? d.current : null, proposed: d.proposed }
        : null,
  };
}

/** Plain-English "what happens if you approve this" line. */
function approvalImpact(p: RoutingProposal): string {
  switch (p.proposal_type) {
    case 'edl_item': {
      const match = existingMatch(p);
      if (match?.dateChange) {
        return `Already on the baseline as "${match.name}". Approving updates its due date from ${match.dateChange.current ?? 'not set'} to ${match.dateChange.proposed}; it does not add a second copy. A date set by an applied change order is kept.`;
      }
      if (match) {
        return `Already on the baseline as "${match.name}". Approving updates that item; it does not add a second copy.`;
      }
      return `Adds this to the Contract baseline under ${edlBucketLabel(p)} the moment you approve.`;
    }
    case 'rubric':
      return 'Adopts this rubric as the scoring definition for its domain across Health Check assessments.';
    case 'checkpoint':
      return 'Adds this checkpoint to the engagement’s IV&V checkpoint schedule.';
    case 'methodology':
      return 'Adopts this as the engagement’s scoring model — the domains and weights every assessment rolls up through.';
    case 'register_framework':
      return 'Adopts this scheme for the engagement’s registers (risk/issue categories and fields).';
    case 'domain_rating':
      return 'Commits an assessment round with this posture rating on the target health domain.';
    case 'budget_entry':
      return 'Records this figure on the engagement budget (spend or change-order layer) the moment you approve.';
    case 'milestone_update':
      return 'Updates the milestone\u2019s forecast date — or creates the milestone — on the Health Check schedule.';
    case 'finding':
      return 'Creates this finding on the Health Check findings register the moment you approve.';
    case 'register_update':
      return 'Applies this change to the linked register entry, with AI provenance recorded.';
    case 'new_record':
      return 'Creates a new register entry from this proposal, with AI provenance recorded.';
    case 'change_order': {
      const n = Array.isArray(asRecord(p.proposed_value).revisions)
        ? (asRecord(p.proposed_value).revisions as unknown[]).length
        : 0;
      return `Registers this document as the current SOW version and applies ${n === 1 ? 'its 1 revision' : `its ${n} revisions`} to the deliverable list. Needs an engagement lead. Use Approve with notes to change the effective date first.`;
    }
    case 'deviation':
      return 'Logs this as an open deviation on the Deviations tab, linked to its deliverable, and starts its response clock.';
    case 'contract_fact': {
      const current = firstString(asRecord(p.proposed_value), ['current_value']);
      return current
        ? `Replaces the confirmed value "${current}" with this one on the Overview and Contract baseline.`
        : 'Confirms this fact. It then shows on the Overview and Contract baseline with its source.';
    }
    case 'checkpoint_link':
      return 'Links the checkpoint to these deliverables. Scoring rounds then show their status, the AI reading of what the vendor submitted, and open deviations, and the AI reads those submissions as evidence.';
    case 'deliverable_submission':
      return 'Links this document to the deliverable as evidence, moves the deliverable to Submitted, and saves this criteria reading for the reviewer. It does not accept or reject the deliverable.';
    default:
      return 'Records your review decision on this proposal.';
  }
}

// ===========================================================================
// Page
// ===========================================================================

type DecisionFilter = 'pending' | 'decided' | 'auto_approved' | 'all';

const DECISION_FILTER_LABELS: Record<DecisionFilter, string> = {
  pending: 'Pending',
  decided: 'Decided',
  auto_approved: 'Auto-approved',
  all: 'All',
};

/** A proposal sent back for revision in this session, kept on screen until its re-read lands. */
interface RevisedProposal {
  proposal: RoutingProposal;
  /** ISO time of the revise decision; a pending proposal of the same type created after it is the re-read. */
  at: string;
}

/** Polling interval for the queue while a revised proposal's re-read is awaited. */
const REVISED_POLL_MS = 15_000;
/**
 * How long a revised card waits for its re-read. The re-run can be skipped
 * (the document's reading was already applied, the module is off), and a
 * card that never clears would poll the queue for the rest of the session.
 */
export const REVISED_MAX_WAIT_MS = 10 * 60_000;

/**
 * Revised proposals that are still waiting: one whose re-read has landed (a
 * pending proposal of the same type, created after the revision) is done,
 * and so is one that has waited longer than REVISED_MAX_WAIT_MS.
 */
export function stillRevising(
  revised: ReadonlyMap<string, RevisedProposal>,
  queued: ReadonlyArray<Pick<RoutingProposal, 'id' | 'proposal_type' | 'decision' | 'created_at'>>,
  now: number = Date.now(),
): Map<string, RevisedProposal> {
  const next = new Map<string, RevisedProposal>();
  for (const [id, r] of revised) {
    if (now - Date.parse(r.at) > REVISED_MAX_WAIT_MS) continue;
    const landed = queued.some(
      (p) =>
        p.id !== id &&
        p.decision === 'pending' &&
        p.proposal_type === r.proposal.proposal_type &&
        p.created_at > r.at,
    );
    if (!landed) next.set(id, r);
  }
  return next;
}

export function ProposalsTab({ focusProposalId }: { focusProposalId?: string } = {}) {
  const { engagementId } = useParams({ from: '/engagements/$engagementId/proposals' });
  // A link to one proposal opens on every decision, so a decided one is found too.
  const [decisionFilter, setDecisionFilter] = useState<DecisionFilter>(
    focusProposalId ? 'all' : 'pending',
  );
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [reviewStateProposalId, setReviewStateProposalId] = useState<string | null>(null);
  // Multi-select for bulk decisions. Only pending rows are selectable; the
  // set is cleared whenever the visible list changes shape (filters).
  const [checkedIds, setCheckedIds] = useState<ReadonlySet<string>>(new Set());
  const [bulkMode, setBulkMode] = useState<'approved_as_noted' | 'rejected' | null>(null);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [bulkSummary, setBulkSummary] = useState<string | null>(null);
  const qc = useQueryClient();

  const apiFilters: ListProposalsFilters = useMemo(() => {
    const out: ListProposalsFilters = {};
    if (decisionFilter === 'pending') out.decision = 'pending';
    if (decisionFilter === 'auto_approved') out.auto_approved = true;
    return out;
  }, [decisionFilter]);

  // Proposals sent back for revision in this session. The pending filter
  // would hide them (they are decided); they stay on screen, marked, until
  // the AI's re-read produces a fresh proposal, and the queue is polled
  // meanwhile.
  const [revised, setRevised] = useState<ReadonlyMap<string, RevisedProposal>>(new Map());
  const queue = useProposalQueueQuery(engagementId, apiFilters, {
    refetchInterval: revised.size > 0 ? REVISED_POLL_MS : false,
  });
  const autoApproveOn = useAiReviewPolicyQuery().data?.auto_approve_proposals === true;

  useEffect(() => {
    if (revised.size === 0 || !queue.data) return;
    const next = stillRevising(revised, queue.data.items);
    if (next.size !== revised.size) setRevised(next);
  }, [queue.data, revised]);

  // Every row the decision filter keeps, before the type filter.
  const unfiltered = useMemo(() => {
    let list = queue.data?.items ?? [];
    if (decisionFilter === 'pending' && revised.size > 0) {
      const present = new Set(list.map((p) => p.id));
      const kept = [...revised.values()].map((r) => r.proposal).filter((p) => !present.has(p.id));
      list = [...list, ...kept];
    }
    if (decisionFilter === 'decided') list = list.filter((p) => p.decision !== 'pending');
    return list;
  }, [queue.data, decisionFilter, revised]);
  const items = useMemo(
    () => (typeFilter ? unfiltered.filter((p) => p.proposal_type === typeFilter) : unfiltered),
    [unfiltered, typeFilter],
  );

  // A type filter whose last row was just decided would leave the queue on
  // an empty list (C2-47): go back to All types instead.
  useEffect(() => {
    if (typeFilter !== null && queue.data && items.length === 0) setTypeFilter(null);
  }, [typeFilter, queue.data, items.length]);

  // Load the Approve-as-Noted reasons with the page, so the first note step
  // opened offers the full list rather than a loading state (C2-48).
  useOverrideTaxonomyQuery();

  // Reset the multi-select when the filters change — a hidden selection that
  // still gets bulk-decided would be a footgun.
  useEffect(() => {
    setCheckedIds(new Set());
    setBulkMode(null);
    setBulkSummary(null);
  }, [decisionFilter, typeFilter]);

  function toggleChecked(id: string): void {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleGroupChecked(groupItems: RoutingProposal[]): void {
    const selectable = groupItems.filter((p) => p.decision === 'pending').map((p) => p.id);
    setCheckedIds((prev) => {
      const next = new Set(prev);
      const allIn = selectable.every((id) => next.has(id));
      for (const id of selectable) {
        if (allIn) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }

  /**
   * Bulk decide: sequential per-proposal decide calls (the API is
   * per-proposal by design — the loop keeps audit + override records exact),
   * fresh idempotency key each, progress surfaced, failures tallied instead
   * of aborting the batch.
   */
  async function runBulk(input: {
    verb: 'approved_as_noted' | 'rejected';
    note: string;
    reasonTag: string;
  }): Promise<void> {
    const ids = items.filter((p) => checkedIds.has(p.id) && p.decision === 'pending');
    if (ids.length === 0) return;
    setBulkProgress({ done: 0, total: ids.length });
    setBulkSummary(null);
    let ok = 0;
    let failed = 0;
    for (const p of ids) {
      try {
        await decideProposal(
          engagementId,
          p.id,
          input.verb === 'approved_as_noted'
            ? {
                verb: 'approved_as_noted',
                override_tag: input.reasonTag,
                decision_rationale: input.note,
                edited_value: asRecord(p.proposed_value),
              }
            : { verb: 'rejected', decision_rationale: input.note },
          crypto.randomUUID(),
        );
        ok += 1;
      } catch {
        failed += 1;
      }
      setBulkProgress({ done: ok + failed, total: ids.length });
    }
    setBulkProgress(null);
    setBulkMode(null);
    setCheckedIds(new Set());
    setBulkSummary(
      failed === 0
        ? `${ok} ${input.verb === 'rejected' ? 'rejected' : 'approved'}.`
        : `${ok} ${input.verb === 'rejected' ? 'rejected' : 'approved'} · ${failed} failed (still in the queue — open one to see why).`,
    );
    void qc.invalidateQueries({ queryKey: ['review-proposals', engagementId] });
    void qc.invalidateQueries({ queryKey: ['registers', engagementId] });
  }

  /** Groups ordered per TYPE_META, rows by confidence (desc) within a group. */
  const groups = useMemo(() => {
    const byType = new Map<string, RoutingProposal[]>();
    for (const p of items) {
      const list = byType.get(p.proposal_type) ?? [];
      list.push(p);
      byType.set(p.proposal_type, list);
    }
    return [...byType.entries()]
      .sort((a, b) => typeMeta(a[0]).order - typeMeta(b[0]).order)
      .map(([type, list]) => {
        const sorted = [...list].sort((a, b) => b.confidence - a.confidence);
        // One document cited by every row is named once, in the group
        // header, instead of on each row.
        const docIds = new Set(sorted.map((p) => p.citations[0]?.document_id ?? null));
        const firstCite = sorted[0]?.citations[0];
        const sharedDocument = docIds.size === 1 && firstCite ? firstCite.document_title : null;
        // Of two rows proposing the same thing, the lower-confidence one
        // (later in the sort) is flagged. A row already approved claims its
        // key first, whatever its confidence, so a pending restatement of it
        // is the one flagged; and a row the API matched to an item on the
        // baseline is flagged on its own (C2-52).
        const seen = new Set<string>(
          sorted.filter((p) => isApproved(p.decision)).map((p) => duplicateKey(p)),
        );
        const duplicates = new Set<string>();
        for (const p of sorted) {
          if (isApproved(p.decision)) continue;
          const key = duplicateKey(p);
          if (seen.has(key) || (p.decision === 'pending' && existingMatch(p) !== null)) {
            duplicates.add(p.id);
          } else {
            seen.add(key);
          }
        }
        return { type, meta: typeMeta(type), items: sorted, sharedDocument, duplicates };
      });
  }, [items]);

  /** Type chips with counts (computed before the type filter is applied). */
  const typeCounts = useMemo(() => {
    const base = queue.data?.items ?? [];
    const scoped =
      decisionFilter === 'decided' ? base.filter((p) => p.decision !== 'pending') : base;
    const counts = new Map<string, number>();
    for (const p of scoped) counts.set(p.proposal_type, (counts.get(p.proposal_type) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => typeMeta(a[0]).order - typeMeta(b[0]).order);
  }, [queue.data, decisionFilter]);

  const selected = useMemo(
    () => items.find((p) => p.id === selectedId) ?? null,
    [selectedId, items],
  );

  // Always have a proposal on screen. When the selection falls out of the
  // list (decided in the Pending view), move to the row that was below it
  // in display order (C2-47), not the first row of the API's order. The
  // order is kept across type filters, so leaving an emptied type lands
  // on the row after that group.
  const displayOrder = useMemo(() => queueDisplayOrder(unfiltered), [unfiltered]);
  const visibleOrder = useMemo(() => queueDisplayOrder(items), [items]);
  const lastOrder = useRef<string[]>([]);
  useEffect(() => {
    if (!selected) {
      if (visibleOrder.length > 0) {
        setSelectedId(nextSelectionAfter(lastOrder.current, selectedId, visibleOrder));
      }
      // Keep the order that still holds the departed row until a new
      // selection lands, so "next" is measured from where it was.
      return;
    }
    lastOrder.current = displayOrder;
  }, [selected, selectedId, visibleOrder, displayOrder]);

  // The linked proposal (T-0239), once, when the queue first lists it.
  const focused = useRef(false);
  useEffect(() => {
    if (!focusProposalId || focused.current || !queue.data) return;
    focused.current = true;
    const row = queue.data.items.find((p) => p.ai_proposal_id === focusProposalId);
    if (row) setSelectedId(row.id);
  }, [focusProposalId, queue.data]);

  const totalShown = items.length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">AI proposal review</h1>
          <p className="text-sm text-muted-foreground">
            {autoApproveOn
              ? 'Auto-approve is on: new AI proposals are approved and applied as they arrive. See Auto-approved for what the system approved.'
              : 'Everything the AI proposed from your documents, waiting for your judgment. Nothing is applied until you approve it here.'}
          </p>
        </div>
        <div className="flex gap-1">
          {(['pending', 'decided', 'auto_approved', 'all'] as const).map((d) => (
            <Button
              key={d}
              type="button"
              size="sm"
              variant={decisionFilter === d ? 'default' : 'outline'}
              onClick={() => setDecisionFilter(d)}
            >
              {DECISION_FILTER_LABELS[d]}
            </Button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        <Button
          type="button"
          size="sm"
          variant={typeFilter === null ? 'secondary' : 'ghost'}
          onClick={() => setTypeFilter(null)}
        >
          All types
        </Button>
        {typeCounts.map(([type, count]) => (
          <Button
            key={type}
            type="button"
            size="sm"
            variant={typeFilter === type ? 'secondary' : 'ghost'}
            onClick={() => setTypeFilter(typeFilter === type ? null : type)}
          >
            {typeMeta(type).group} · {count}
          </Button>
        ))}
      </div>

      {bulkSummary ? (
        <div className="rounded-md border border-emerald-300 bg-emerald-50/60 px-3 py-2 text-sm text-emerald-900">
          {bulkSummary}
        </div>
      ) : null}

      {checkedIds.size > 0 ? (
        <div className="rounded-md border border-primary/30 bg-primary/5 p-3">
          {bulkMode === null ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">{checkedIds.size} selected</span>
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setCheckedIds(new Set())}
                >
                  Clear
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  onClick={() => setBulkMode('rejected')}
                >
                  Reject selected…
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="bg-emerald-600 text-white hover:bg-emerald-700"
                  onClick={() => setBulkMode('approved_as_noted')}
                >
                  Approve selected…
                </Button>
              </div>
            </div>
          ) : (
            <BulkDecideBar
              mode={bulkMode}
              count={checkedIds.size}
              progress={bulkProgress}
              onCancel={() => setBulkMode(null)}
              onSubmit={(v) => void runBulk(v)}
            />
          )}
        </div>
      ) : null}

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-5">
        <Card className="lg:col-span-2 overflow-hidden p-0">
          {queue.isLoading ? (
            <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
              <Spinner /> Loading proposals…
            </div>
          ) : queue.error ? (
            <div className="p-4">
              <ErrorMessage error={queue.error} />
            </div>
          ) : totalShown === 0 ? (
            <div className="p-4">
              <EmptyState
                title="Nothing to review"
                description="No proposals match the current filters."
              />
            </div>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto">
              {groups.map((g) => (
                <div key={g.type}>
                  <div className="sticky top-0 z-10 flex items-start justify-between gap-2 border-b bg-muted/80 px-4 py-2 backdrop-blur">
                    <div className="min-w-0">
                      <div
                        className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
                        title={g.meta.groupHint || undefined}
                      >
                        {g.meta.group} · {g.items.length}
                      </div>
                      {g.sharedDocument ? (
                        <div
                          className="truncate text-[11px] text-muted-foreground/80"
                          title={g.sharedDocument}
                          data-testid="group-document"
                        >
                          From {g.sharedDocument}
                        </div>
                      ) : g.meta.groupHint ? (
                        <div className="text-[11px] text-muted-foreground/80">
                          {g.meta.groupHint}
                        </div>
                      ) : null}
                    </div>
                    {g.items.some((p) => p.decision === 'pending') ? (
                      <button
                        type="button"
                        onClick={() => toggleGroupChecked(g.items)}
                        className="shrink-0 text-[11px] font-medium text-primary hover:underline"
                      >
                        {g.items
                          .filter((p) => p.decision === 'pending')
                          .every((p) => checkedIds.has(p.id))
                          ? 'Clear all'
                          : 'Select all'}
                      </button>
                    ) : null}
                  </div>
                  {g.items.map((p) => (
                    <QueueRow
                      key={p.id}
                      proposal={p}
                      revised={revised.has(p.id)}
                      selected={p.id === selectedId}
                      onSelect={() => setSelectedId(p.id)}
                      checked={checkedIds.has(p.id)}
                      onToggleCheck={
                        p.decision === 'pending' && !revised.has(p.id)
                          ? () => toggleChecked(p.id)
                          : undefined
                      }
                      omitDocument={g.sharedDocument !== null}
                      duplicateNote={g.duplicates.has(p.id) ? duplicateNoteFor(p) : null}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
        </Card>

        <div className="lg:col-span-3">
          {selected ? (
            <ProposalDetail
              key={selected.id}
              engagementId={engagementId}
              proposal={selected}
              revised={revised.has(selected.id)}
              onRevised={(p, note) =>
                setRevised((prev) => {
                  const next = new Map(prev);
                  next.set(p.id, {
                    proposal: { ...p, decision: 'revise', decision_rationale: note },
                    at: new Date().toISOString(),
                  });
                  return next;
                })
              }
              onChangeReviewState={() => setReviewStateProposalId(selected.id)}
            />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>No proposal selected</CardTitle>
                <CardDescription>Pick an item from the queue on the left.</CardDescription>
              </CardHeader>
            </Card>
          )}
        </div>
      </div>

      {reviewStateProposalId ? (
        <ReviewStateModal
          engagementId={engagementId}
          proposalId={reviewStateProposalId}
          open={reviewStateProposalId !== null}
          onOpenChange={(next) => {
            if (!next) setReviewStateProposalId(null);
          }}
        />
      ) : null}
    </div>
  );
}

// ===========================================================================
// Queue row
// ===========================================================================

function QueueRow({
  proposal,
  revised = false,
  selected,
  onSelect,
  checked,
  onToggleCheck,
  omitDocument = false,
  duplicateNote = null,
}: {
  proposal: RoutingProposal;
  /** Sent back for revision in this session; the AI is re-reading the source. */
  revised?: boolean;
  selected: boolean;
  onSelect: () => void;
  checked: boolean;
  onToggleCheck?: () => void;
  /** The group header already names the one document every row cites. */
  omitDocument?: boolean;
  /** Set when a row above in the same group proposes the same thing. */
  duplicateNote?: string | null;
}) {
  const headline = proposalHeadline(proposal);
  const { label, value } = rowTitle(proposal);
  const meta = rowMeta(proposal, { omitDocument });
  const decisionBadge = revised ? (
    <Badge variant="warning" className="text-[10px]">
      Revised, re-reading…
    </Badge>
  ) : proposal.decision !== 'pending' ? (
    <DecisionBadge decision={proposal.decision} autoApproved={proposal.auto_approved} />
  ) : null;
  const hasSecondLine = meta !== null || proposal.is_baseline || decisionBadge !== null;
  return (
    <div
      className={cn(
        'flex w-full items-start gap-2 border-b px-3 py-2 transition-colors hover:bg-muted/40',
        selected && 'bg-primary/5 shadow-[inset_2px_0_0] shadow-primary',
      )}
    >
      {onToggleCheck ? (
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggleCheck}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${headline}`}
          className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-emerald-600"
        />
      ) : (
        <span className="mt-1 h-4 w-4 shrink-0" aria-hidden="true" />
      )}
      <button
        type="button"
        onClick={onSelect}
        aria-label={headline}
        aria-current={selected ? 'true' : undefined}
        className="min-w-0 flex-1 text-left"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="line-clamp-2 min-w-0 flex-1 text-sm leading-snug">
            {label ? <span className="text-muted-foreground">{label} · </span> : null}
            <span className="font-medium text-foreground">{value}</span>
          </div>
          <AiConfidenceChip value={proposal.confidence} className="shrink-0" />
        </div>
        {hasSecondLine ? (
          <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] text-muted-foreground">
            {proposal.is_baseline ? (
              <Badge variant="outline" className="text-[10px]">
                Baseline
              </Badge>
            ) : null}
            {decisionBadge}
            {meta ? <span className="truncate">{meta}</span> : null}
          </div>
        ) : null}
        {duplicateNote ? (
          <div className="mt-1 text-[11px] text-amber-700" data-testid="duplicate-note">
            {duplicateNote}
          </div>
        ) : null}
      </button>
    </div>
  );
}

/**
 * The row's one line: for a contract fact the fact's label (muted) and its
 * value; for everything else the headline. The full text lives in the
 * detail pane — the row clamps to two lines.
 */
function rowTitle(p: RoutingProposal): { label: string | null; value: string } {
  if (p.proposal_type === 'contract_fact') {
    const v = asRecord(p.proposed_value);
    const label = firstString(v, ['label']);
    const value = firstString(v, ['value']);
    if (label && value) return { label, value };
  }
  return { label: null, value: proposalHeadline(p) };
}

/**
 * The second line on a queue card: the facts a reviewer weighs before
 * approving, without opening the row — per-type payload facts plus the
 * source document + page the proposal cites (unless the group header
 * already names the document every row cites).
 */
function rowMeta(p: RoutingProposal, opts: { omitDocument?: boolean } = {}): string | null {
  const v = asRecord(p.proposed_value);
  const bits: string[] = [];
  switch (p.proposal_type) {
    case 'edl_item': {
      const change = existingMatch(p)?.dateChange ?? null;
      if (change) bits.push(`Updates due date ${change.current ?? 'not set'} → ${change.proposed}`);
      const bucket = edlBucketLabel(p);
      if (bucket !== 'Deliverables') bits.push(`→ ${bucket}`);
      const cat = firstString(v, ['category']);
      if (cat) bits.push(humanize(cat));
      if (typeof v.required === 'boolean') bits.push(v.required ? 'Required' : 'Optional');
      const domain = firstString(v, ['domain']);
      if (domain) bits.push(humanize(domain));
      break;
    }
    case 'checkpoint': {
      const timing = firstString(v, ['timing', 'schedule', 'cadence']);
      if (timing) bits.push(humanize(timing));
      const focus = Array.isArray(v.focus) ? v.focus.length : 0;
      if (focus > 0) bits.push(`${focus} focus area${focus === 1 ? '' : 's'}`);
      break;
    }
    case 'rubric': {
      const bands = Array.isArray(v.criteria) ? v.criteria.length : 0;
      if (bands > 0) bits.push(`${bands} scoring band${bands === 1 ? '' : 's'}`);
      const scale = firstString(v, ['scale']);
      if (scale) bits.push(`scale ${scale}`);
      break;
    }
    case 'methodology': {
      const domains = Array.isArray(v.domains) ? v.domains.length : 0;
      if (domains > 0) bits.push(`${domains} domains`);
      break;
    }
    case 'change_order': {
      const revs = Array.isArray(v.revisions) ? v.revisions.map(asRecord) : [];
      const count = (t: string) => revs.filter((r) => r.change_type === t).length;
      bits.push(
        `${count('added')} added · ${count('modified')} modified · ${count('removed')} removed`,
      );
      const eff = firstString(v, ['effective_date']);
      bits.push(eff ? `effective ${eff}` : 'no effective date found');
      break;
    }
    case 'contract_fact': {
      if (firstString(v, ['document_type']) === 'change_order') {
        bits.push('revision from a change order');
      }
      const current = firstString(v, ['current_value']);
      if (current) bits.push(`currently ${current}`);
      break;
    }
    case 'deliverable_submission': {
      const readings = Array.isArray(v.criteria) ? v.criteria.map(asRecord) : [];
      if (readings.length === 0) {
        bits.push('no acceptance criteria on this deliverable');
      } else {
        const count = (verdict: string) => readings.filter((r) => r.verdict === verdict).length;
        bits.push(
          `${count('met')} met · ${count('partially_met')} partial · ${count('not_met')} not met · ${count('not_evidenced')} not shown`,
        );
      }
      break;
    }
    case 'deviation': {
      const sev = firstString(v, ['severity']);
      if (sev) bits.push(`${sev} severity`);
      const type = firstString(v, ['deviation_type']);
      if (type) bits.push(deviationTypeLabel(type));
      const del = firstString(v, ['deliverable_name']);
      if (del) bits.push(del);
      break;
    }
    default:
      break;
  }
  const cite = p.citations[0];
  if (cite && !opts.omitDocument) {
    const doc = shortDocLabel(cite.document_title);
    bits.push(cite.page_number !== null ? `${doc} · p.${cite.page_number}` : doc);
  }
  return bits.length > 0 ? bits.join(' · ') : null;
}

/**
 * What makes two rows in one group the same proposal: the same contract
 * fact (one value can stand per fact), else the same headline. The
 * lower-confidence row is flagged so a reviewer does not approve both.
 */
export function duplicateKey(p: RoutingProposal): string {
  if (p.proposal_type === 'contract_fact') {
    const key = firstString(asRecord(p.proposed_value), ['fact_key', 'label']);
    if (key) return `fact:${key.toLowerCase()}`;
  }
  return `headline:${proposalHeadline(p).replace(/\s+/g, ' ').trim().toLowerCase()}`;
}

function isApproved(decision: RoutingProposal['decision']): boolean {
  return decision === 'approved' || decision === 'approved_as_noted';
}

function duplicateNoteFor(p: RoutingProposal): string {
  if (p.proposal_type === 'contract_fact') {
    const label = firstString(asRecord(p.proposed_value), ['label']) ?? 'this fact';
    return `Another value for ${label} is proposed above. Only one can stand.`;
  }
  const match = p.proposal_type === 'edl_item' ? existingMatch(p) : null;
  if (match) return `Already on the baseline as "${match.name}" (approving updates it).`;
  return 'Looks like a duplicate of an item above.';
}

/**
 * Shared-note step for a bulk decision: one reason + one sentence applied to
 * every selected proposal. Mirrors DecisionNoteBar semantics (≥10-char note,
 * taxonomy reason for approvals, loud validation) with batch progress.
 */
function BulkDecideBar({
  mode,
  count,
  progress,
  onSubmit,
  onCancel,
}: {
  mode: 'approved_as_noted' | 'rejected';
  count: number;
  progress: { done: number; total: number } | null;
  onSubmit: (input: {
    verb: 'approved_as_noted' | 'rejected';
    note: string;
    reasonTag: string;
  }) => void;
  onCancel: () => void;
}) {
  const isApprove = mode === 'approved_as_noted';
  const [reasonTag, setReasonTag] = useState('other');
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState(false);
  const busy = progress !== null;
  const noteOk = note.trim().length >= 10;

  function submit() {
    if (busy) return;
    if (!noteOk) {
      setNoteError(true);
      return;
    }
    setNoteError(false);
    onSubmit({ verb: mode, note: note.trim(), reasonTag });
  }

  return (
    <div>
      <div className="text-sm font-semibold">
        {isApprove
          ? `Approve ${count} proposals — one shared note`
          : `Reject ${count} proposals — say why`}
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground">
        {isApprove
          ? 'The same note and reason are recorded on every selected item.'
          : 'The same reason is recorded on every selected item.'}
      </p>
      <textarea
        // eslint-disable-next-line jsx-a11y/no-autofocus -- the note is the sole purpose of this step
        autoFocus
        value={note}
        onChange={(e) => {
          setNote(e.target.value);
          if (e.target.value.trim().length >= 10) setNoteError(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        rows={2}
        disabled={busy}
        className="mt-2 w-full rounded-md border bg-background p-2 text-sm"
        placeholder={
          isApprove
            ? 'e.g. Matches the deliverables in the signed change order.'
            : 'e.g. Not part of the contracted scope.'
        }
      />
      {isApprove ? (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          <ReasonTagSelect
            id="bulk_reason"
            value={reasonTag}
            onChange={setReasonTag}
            disabled={busy}
          />
        </div>
      ) : null}
      {noteError ? (
        <div className="mt-2 text-xs font-semibold text-destructive">
          Your note needs at least 10 characters — one short sentence.
        </div>
      ) : null}
      <div className="mt-3 flex items-center justify-between gap-2">
        <span className="text-[11px] text-muted-foreground">
          {busy
            ? `Working… ${progress.done}/${progress.total}`
            : noteOk
              ? 'Enter to confirm'
              : 'A few more words needed'}
        </span>
        <div className="flex items-center gap-2">
          {busy ? <Spinner /> : null}
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={isApprove ? 'default' : 'destructive'}
            className={isApprove ? 'bg-emerald-600 text-white hover:bg-emerald-700' : undefined}
            onClick={submit}
            disabled={busy}
          >
            {isApprove ? `Approve ${count}` : `Reject ${count}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Detail pane
// ===========================================================================

function ProposalDetail({
  engagementId,
  proposal,
  revised = false,
  onRevised,
  onChangeReviewState,
}: {
  engagementId: string;
  proposal: RoutingProposal;
  /** Sent back for revision in this session; the AI is re-reading the source. */
  revised?: boolean;
  onRevised: (proposal: RoutingProposal, note: string) => void;
  onChangeReviewState: () => void;
}) {
  const source = useProposalSourceQuery(engagementId, proposal.id);
  const decide = useDecideProposalMutation(engagementId);
  const { toast } = useToast();

  // The decision flow lives at the TOP of the card, beside the title, so
  // the verbs are on screen for every proposal without scrolling past the
  // evidence. Clicking "Approve as Noted" / "Reject" / "Revise & Resubmit"
  // swaps the verb row for a single note step (reason + one sentence) in
  // the same place instead of opening a second form elsewhere. `noteStep`
  // is which verb the note is being written for.
  const [noteStep, setNoteStep] = useState<NoteStep | null>(null);

  function onClickVerb(verb: DecideVerb) {
    if (verb === 'approved_as_noted' || verb === 'rejected' || verb === 'revise') {
      setNoteStep(verb);
      return;
    }
    decide.mutate({ proposalId: proposal.id, input: { verb } });
  }

  function submitNote(input: { reasonTag: string; note: string; editedValue: unknown }) {
    if (noteStep === 'revise') {
      decide.mutate(
        { proposalId: proposal.id, input: { verb: 'revise', decision_rationale: input.note } },
        {
          onSuccess: () => {
            setNoteStep(null);
            onRevised(proposal, input.note);
            toast({
              message: 'Sent back for revision — the AI is re-reading the source with your note.',
            });
          },
        },
      );
    } else if (noteStep === 'approved_as_noted') {
      decide.mutate(
        {
          proposalId: proposal.id,
          input: {
            verb: 'approved_as_noted',
            override_tag: input.reasonTag,
            decision_rationale: input.note,
            edited_value: input.editedValue as Record<string, unknown>,
          },
        },
        { onSuccess: () => setNoteStep(null) },
      );
    } else if (noteStep === 'rejected') {
      decide.mutate(
        { proposalId: proposal.id, input: { verb: 'rejected', decision_rationale: input.note } },
        { onSuccess: () => setNoteStep(null) },
      );
    }
  }

  const meta = typeMeta(proposal.proposal_type);
  const canDecide = proposal.decision === 'pending' && !revised;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant="secondary">{proposal.label ?? meta.label}</Badge>
            <AiConfidenceChip value={proposal.confidence} />
            {proposal.is_baseline ? (
              <Badge variant="outline">Baseline — review required</Badge>
            ) : null}
            {proposal.decision !== 'pending' ? (
              <DecisionBadge decision={proposal.decision} autoApproved={proposal.auto_approved} />
            ) : null}
          </div>
          {canDecide && noteStep === null ? (
            <ActionBar
              isBaseline={proposal.is_baseline}
              pending={decide.isPending}
              onApprove={() => onClickVerb('approved')}
              onApprovedAsNoted={() => onClickVerb('approved_as_noted')}
              onRevise={() => onClickVerb('revise')}
              onReject={() => onClickVerb('rejected')}
            />
          ) : null}
        </div>
        <CardTitle className="mt-2 text-lg leading-snug">{detailTitle(proposal)}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {canDecide && noteStep !== null ? (
          <DecisionNoteBar
            key={`${proposal.id}:${noteStep}`}
            mode={noteStep}
            proposalType={proposal.proposal_type}
            proposedValue={proposal.proposed_value}
            pending={decide.isPending}
            onSubmit={submitNote}
            onCancel={() => setNoteStep(null)}
            error={
              decide.error instanceof ApiError && decide.error.code === 'validation'
                ? decide.error
                : null
            }
          />
        ) : null}

        {decide.error ? <DecideError error={decide.error} /> : null}

        {revised ? (
          <div
            className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
            data-testid="revised-notice"
          >
            <span className="font-semibold">Revised, re-reading…</span> The AI is re-reading the
            source with your note. Its new proposal will appear in this queue; this one stays here
            until it does.
            {proposal.decision_rationale ? (
              <div className="mt-1 italic">“{proposal.decision_rationale}”</div>
            ) : null}
          </div>
        ) : proposal.decision !== 'pending' ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
            <div className="flex items-center justify-between gap-2">
              <div>
                <span className="font-semibold">
                  Decided — {titleCase(proposal.decision.replace(/_/g, ' '))}
                </span>
                {proposal.auto_approved ? (
                  <div className="mt-1">
                    Approved automatically under the firm&rsquo;s auto-approve setting. No one
                    reviewed it first.
                  </div>
                ) : proposal.decision_rationale ? (
                  <div className="mt-1">{proposal.decision_rationale}</div>
                ) : null}
              </div>
              <Button type="button" size="sm" variant="outline" onClick={onChangeReviewState}>
                Change
              </Button>
            </div>
          </div>
        ) : null}

        <ProposalClaim proposal={proposal} />

        <SourcePanel
          engagementId={engagementId}
          proposal={proposal}
          source={source.data}
          isLoading={source.isLoading}
          error={source.error}
        />

        <footer className="space-y-2 border-t pt-3 text-xs text-muted-foreground">
          {proposal.decision === 'pending' ? (
            <p className="leading-relaxed" data-testid="approval-impact">
              <span className="font-medium text-foreground">If you approve:</span>{' '}
              {approvalImpact(proposal)}
              {proposal.is_baseline && canDecide
                ? ' Baseline items need one short note with your approval (firm policy).'
                : ''}
            </p>
          ) : null}
          <details className="rounded-md border bg-muted/20">
            <summary className="cursor-pointer select-none px-3 py-1.5 font-medium">
              Proposal details
            </summary>
            <div className="space-y-2 px-3 pb-3">
              <div>
                Proposed {formatDate(proposal.created_at)} ·{' '}
                <span className="font-mono">{proposal.id}</span>
              </div>
              <div>
                <Label className="text-[10px] uppercase text-muted-foreground">
                  Proposed value
                </Label>
                <pre className="mt-1 max-h-56 overflow-auto whitespace-pre-wrap break-all rounded-md border bg-background p-2">
                  {JSON.stringify(proposal.proposed_value, null, 2)}
                </pre>
              </div>
              {proposal.target_ref ? (
                <div>
                  <Label className="text-[10px] uppercase text-muted-foreground">
                    Target reference
                  </Label>
                  <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded-md border bg-background p-2">
                    {JSON.stringify(proposal.target_ref, null, 2)}
                  </pre>
                </div>
              ) : null}
            </div>
          </details>
        </footer>
      </CardContent>
    </Card>
  );
}

/** The detail title: a contract fact's label; otherwise the headline. */
function detailTitle(p: RoutingProposal): string {
  if (p.proposal_type === 'contract_fact') {
    return firstString(asRecord(p.proposed_value), ['label']) ?? 'Contract fact';
  }
  return proposalHeadline(p);
}

/**
 * The claim under the title, readable and unboxed: a contract fact's value
 * and what the baseline holds today; other types' fields via
 * ProposedValueFields. Provenance (which document) is the evidence
 * section's job and is never shown here as if it were proposed.
 */
function ProposalClaim({ proposal }: { proposal: RoutingProposal }) {
  if (proposal.proposal_type === 'contract_fact') {
    const v = asRecord(proposal.proposed_value);
    const value = firstString(v, ['value']);
    const current = firstString(v, ['current_value']);
    // C2-49: a change order revises the contract value, end date or
    // go-live; approving replaces the baseline fact, so the card says so.
    const fromChangeOrder = firstString(v, ['document_type']) === 'change_order';
    return (
      <section aria-label="Proposed value" className="space-y-1.5">
        {fromChangeOrder ? (
          <p className="text-xs font-medium text-muted-foreground">
            Revision from a change order. Approving replaces the value on the baseline.
          </p>
        ) : null}
        {value ? <p className="text-base leading-relaxed">{value}</p> : null}
        <p className="text-xs text-muted-foreground">
          {current ? (
            <>
              Currently on the baseline: <span className="text-foreground">{current}</span>
            </>
          ) : (
            'Not on the contract baseline yet.'
          )}
        </p>
      </section>
    );
  }
  return <ProposedValueFields proposal={proposal} />;
}

// ===========================================================================
// Readable rendering of proposed_value (never raw JSON)
// ===========================================================================

interface FieldRow {
  label: string;
  value: string;
}

/**
 * Tolerant per-type field extraction. The payload is model-generated jsonb,
 * so every access is defensive; unknown shapes degrade to primitive
 * key/value rows rather than JSON dumps.
 */
function extractFields(p: RoutingProposal): {
  rows: FieldRow[];
  criteria: string[];
  /** Per-type itemised list (e.g. a change order's revisions), with its heading. */
  list: { heading: string; items: string[] } | null;
} {
  const v = asRecord(p.proposed_value);
  const rows: FieldRow[] = [];
  const criteria: string[] = [];
  let list: { heading: string; items: string[] } | null = null;
  const skip = new Set(['name', 'title', 'summary']); // already in the headline

  // Change order: the summary is the substance (the headline is only the
  // document name), then one line per proposed revision — what changes,
  // on which deliverable, and why. Ids are never shown.
  if (p.proposal_type === 'change_order') {
    const summary = firstString(v, ['summary']);
    if (summary) rows.push({ label: 'Summary', value: summary });
    rows.push({
      label: 'Effective date',
      value: firstString(v, ['effective_date']) ?? 'Not found in the document',
    });
    const items: string[] = [];
    for (const raw of Array.isArray(v.revisions) ? v.revisions : []) {
      const r = asRecord(raw);
      const name = firstString(r, ['name']) ?? 'Deliverable';
      const verb =
        r.change_type === 'added' ? 'Add' : r.change_type === 'removed' ? 'Remove' : 'Change';
      const changes = Object.entries(asRecord(r.changes))
        .filter(([k]) => k !== 'name')
        .map(
          ([k, val]) =>
            `${humanize(k).toLowerCase()} → ${typeof val === 'boolean' ? (val ? 'yes' : 'no') : String(val)}`,
        )
        .join('; ');
      const reason = firstString(r, ['reason']);
      items.push(`${verb}: ${name}${changes ? ` (${changes})` : ''}${reason ? `. ${reason}` : ''}`);
    }
    list = {
      heading: items.length > 0 ? 'Proposed revisions' : 'No deliverable revisions found',
      items,
    };
    return { rows, criteria, list };
  }
  if (p.proposal_type === 'checkpoint_link') {
    const reason = firstString(v, ['reason']);
    if (reason) rows.push({ label: 'Why', value: reason });
    list = {
      heading: 'Deliverables',
      items: (Array.isArray(v.deliverables) ? v.deliverables : []).map(
        (d) => firstString(asRecord(d), ['name']) ?? 'Deliverable',
      ),
    };
    return { rows, criteria, list };
  }
  if (p.proposal_type === 'contract_fact') {
    rows.push({
      label: firstString(v, ['label']) ?? 'Fact',
      value: firstString(v, ['value']) ?? '',
    });
    const current = firstString(v, ['current_value']);
    if (current) rows.push({ label: 'Currently', value: current });
    // document_title is provenance, not a proposed value: the evidence
    // section names the document.
    return { rows, criteria, list };
  }
  if (p.proposal_type === 'deliverable_submission') {
    for (const [label, key] of [
      ['Document', 'document_title'],
      ['Deliverable', 'deliverable_name'],
      ['Why it matches', 'match_reason'],
    ] as const) {
      const val = firstString(v, [key]);
      if (val) rows.push({ label, value: val });
    }
    const items = (Array.isArray(v.criteria) ? v.criteria : []).map((raw) => {
      const r = asRecord(raw);
      const verdict = VERDICT_LABELS[String(r.verdict)] ?? 'Not shown in the document';
      const note = firstString(r, ['note']);
      return `${verdict}: ${firstString(r, ['criterion']) ?? 'Criterion'}${note ? `. ${note}` : ''}`;
    });
    list = {
      heading:
        items.length > 0
          ? 'AI reading against the acceptance criteria'
          : 'This deliverable has no acceptance criteria to read against',
      items,
    };
    return { rows, criteria, list };
  }
  if (p.proposal_type === 'deviation') {
    for (const [label, key] of [
      ['Severity', 'severity'],
      ['Type', 'deviation_type'],
      ['Deliverable', 'deliverable_name'],
      ['What the document shows', 'description'],
      ['Source document', 'source_document_title'],
    ] as const) {
      const val = firstString(v, [key]);
      if (val)
        rows.push({ label, value: key === 'deviation_type' ? deviationTypeLabel(val) : val });
    }
    return { rows, criteria, list };
  }
  // Finding (T-0235): the domain and source document by name; ids are
  // never shown.
  if (p.proposal_type === 'finding') {
    for (const [label, key] of [
      ['Severity', 'severity'],
      ['Type', 'category'],
      ['Domain', 'health_domain_name'],
      ['What the document shows', 'description'],
      ['Source document', 'source_document_title'],
    ] as const) {
      const val = firstString(v, [key]);
      if (val) rows.push({ label, value: key === 'severity' ? humanize(val) : val });
    }
    return { rows, criteria, list };
  }

  const pushPrimitive = (label: string, raw: unknown) => {
    if (raw === null || raw === undefined) return;
    if (typeof raw === 'boolean') rows.push({ label, value: raw ? 'Yes' : 'No' });
    else if (typeof raw === 'number') rows.push({ label, value: String(raw) });
    else if (typeof raw === 'string' && raw.trim()) rows.push({ label, value: humanize(raw) });
  };

  // Rubric criteria: render "score — definition" lines.
  const rawCriteria = v.criteria;
  if (Array.isArray(rawCriteria)) {
    for (const c of rawCriteria) {
      const cr = asRecord(c);
      const score = typeof cr.score === 'number' || typeof cr.score === 'string' ? cr.score : null;
      const def = firstString(cr, ['definition', 'description', 'label']);
      if (def) criteria.push(score !== null ? `${score} — ${def}` : def);
    }
  }

  // Methodology domains: summarize name + weight.
  const rawDomains = v.domains;
  if (Array.isArray(rawDomains)) {
    const parts: string[] = [];
    for (const d of rawDomains) {
      const dr = asRecord(d);
      const name =
        firstString(dr, ['name', 'domain', 'label']) ?? (typeof d === 'string' ? d : null);
      if (!name) continue;
      const weight = typeof dr.weight === 'number' ? ` (${Math.round(dr.weight * 100)}%)` : '';
      parts.push(`${humanize(name)}${weight}`);
    }
    if (parts.length > 0) rows.push({ label: 'Domains', value: parts.join(' · ') });
  }

  for (const [key, raw] of Object.entries(v)) {
    if (skip.has(key) || key === 'criteria' || key === 'domains') continue;
    pushPrimitive(humanize(key), raw);
  }

  return { rows, criteria, list };
}

function ProposedValueFields({ proposal }: { proposal: RoutingProposal }) {
  const { rows, criteria, list } = extractFields(proposal);
  if (rows.length === 0 && criteria.length === 0 && list === null) return null;
  return (
    <section aria-label="Proposed value" className="space-y-3">
      {rows.length > 0 ? (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
          {rows.map((r) => (
            <div key={r.label} className="flex min-w-0 gap-2">
              <dt className="w-28 shrink-0 text-muted-foreground">{r.label}</dt>
              <dd className="min-w-0 break-words font-medium">{r.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {list ? (
        <div>
          <div className="text-xs font-medium text-muted-foreground">{list.heading}</div>
          {list.items.length > 0 ? (
            <ul className="mt-1 list-disc space-y-1 pl-4 text-sm">
              {list.items.map((item, i) => (
                <li key={i} className="break-words leading-snug">
                  {item}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {criteria.length > 0 ? (
        <div>
          <div className="text-xs font-medium text-muted-foreground">Scoring bands</div>
          <ul className="mt-1 space-y-1 text-sm">
            {criteria.map((c) => (
              <li key={c} className="break-words leading-snug">
                {c}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

// ===========================================================================
// Sources
// ===========================================================================

function SourcePanel({
  engagementId,
  proposal,
  source,
  isLoading,
  error,
}: {
  engagementId: string;
  proposal: RoutingProposal;
  source: { citations: SourceCitationDto[] } | undefined;
  isLoading: boolean;
  error: unknown;
}) {
  const [openCite, setOpenCite] = useState<CitationChipData | null>(null);
  const places = citationPlaces(proposal);
  const passages = useMemo(() => sourcePassages(source?.citations ?? []), [source]);
  const [first, ...rest] = passages;

  return (
    <section aria-label="Where this came from" className="space-y-2 border-t pt-3">
      <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        <span>Read from</span>
        {places.length === 0 ? <span>no cited source</span> : null}
        {places.map((cite) => (
          <CitationChip key={cite.id} cite={cite} onOpen={() => setOpenCite(cite)} />
        ))}
        {passages.length > 1 ? <span>{passages.length} passages</span> : null}
      </div>
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner /> Loading passages…
        </div>
      ) : error ? (
        <ErrorMessage error={error} />
      ) : !first ? (
        <div className="text-xs text-muted-foreground">No cited passages available.</div>
      ) : (
        <>
          <Passage citation={first} />
          {rest.length > 0 ? (
            <details className="text-xs">
              <summary className="cursor-pointer select-none text-muted-foreground">
                Show {rest.length} more passage{rest.length === 1 ? '' : 's'}
              </summary>
              <div className="mt-2 space-y-2">
                {rest.map((c) => (
                  <Passage key={c.citation_id} citation={c} />
                ))}
              </div>
            </details>
          ) : null}
        </>
      )}
      {openCite ? (
        <CitationSourceModal
          engagementId={engagementId}
          cite={openCite}
          open
          onOpenChange={(o) => {
            if (!o) setOpenCite(null);
          }}
        />
      ) : null}
    </section>
  );
}

function Passage({ citation: c }: { citation: SourceCitationDto }) {
  return (
    <blockquote className="rounded-md border-l-2 border-amber-400 bg-amber-50/60 p-2 text-xs leading-relaxed">
      {c.chunk_text ? (
        <HighlightedPassage text={c.chunk_text} span={c.span} />
      ) : (
        <span className="italic text-muted-foreground">Passage text unavailable.</span>
      )}
      <footer className="mt-1 text-[10px] text-muted-foreground">
        {shortDocLabel(c.document_title)}
        {c.section_path ? ` · ${c.section_path}` : ''}
        {c.page_number !== null ? ` · p.${c.page_number}` : ''}
      </footer>
    </blockquote>
  );
}

/**
 * One chip per place cited. Citations that share a document, section and
 * page collapse into the first of them: a fact read from five chunks of
 * one payment schedule is one place, not five identical chips.
 */
export function citationPlaces(proposal: RoutingProposal): CitationChipData[] {
  const seen = new Set<string>();
  const out: CitationChipData[] = [];
  for (const c of proposal.citations) {
    const key = `${c.document_id}|${c.section_path ?? ''}|${c.page_number ?? ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      id: c.id,
      doc_label: shortDocLabel(c.document_title),
      section: c.section_path,
      page: c.page_number,
      confidence: proposal.confidence,
    });
  }
  return out;
}

/**
 * The passages worth showing: citations that resolved to the same chunk
 * collapse into one, and a passage with a located quote comes first so the
 * best evidence is the one on screen; the rest sit behind a disclosure.
 */
export function sourcePassages(citations: SourceCitationDto[]): SourceCitationDto[] {
  const seen = new Set<string>();
  const unique: SourceCitationDto[] = [];
  for (const c of citations) {
    const key = c.chunk_id ?? c.chunk_text ?? c.citation_id;
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(c);
  }
  const rank = (c: SourceCitationDto) =>
    c.chunk_text && spanOffsets(c.span, c.chunk_text.length) ? 0 : 1;
  return unique
    .map((c, i) => ({ c, i }))
    .sort((a, b) => rank(a.c) - rank(b.c) || a.i - b.i)
    .map((x) => x.c);
}

function shortDocLabel(title: string): string {
  return title.length > 34 ? `${title.slice(0, 32)}…` : title;
}

/**
 * The cited passage with the model's quote marked (production issue 8). The
 * span is `{start, end}` offsets into the passage, from the server, when the
 * quote was located in the chunk; otherwise the whole passage is shown plain.
 */
function HighlightedPassage({ text, span }: { text: string; span: unknown }) {
  const offsets = spanOffsets(span, text.length);
  if (!offsets) return <span className="break-words">{text}</span>;
  return (
    <span className="break-words">
      {text.slice(0, offsets.start)}
      <mark className="rounded-sm bg-amber-200/80 px-0.5 text-inherit" data-testid="cited-quote">
        {text.slice(offsets.start, offsets.end)}
      </mark>
      {text.slice(offsets.end)}
    </span>
  );
}

function spanOffsets(span: unknown, length: number): { start: number; end: number } | null {
  if (!span || typeof span !== 'object' || Array.isArray(span)) return null;
  const { start, end } = span as Record<string, unknown>;
  if (typeof start !== 'number' || typeof end !== 'number') return null;
  const s = Math.max(0, Math.min(Math.trunc(start), length));
  const e = Math.max(s, Math.min(Math.trunc(end), length));
  return e > s && !(s === 0 && e === length) ? { start: s, end: e } : null;
}

// ===========================================================================
// Actions
// ===========================================================================

function ActionBar({
  isBaseline,
  pending,
  onApprove,
  onApprovedAsNoted,
  onRevise,
  onReject,
}: {
  isBaseline: boolean;
  pending: boolean;
  onApprove: () => void;
  onApprovedAsNoted: () => void;
  onRevise: () => void;
  onReject: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="action-bar">
      {pending ? <Spinner /> : null}
      <Button type="button" size="sm" variant="outline" onClick={onRevise} disabled={pending}>
        Revise &amp; Resubmit
      </Button>
      <Button type="button" size="sm" variant="destructive" onClick={onReject} disabled={pending}>
        Reject
      </Button>
      {isBaseline ? (
        <Button
          type="button"
          size="sm"
          onClick={onApprovedAsNoted}
          disabled={pending}
          className="bg-emerald-600 text-white hover:bg-emerald-700"
        >
          Approve as Noted
        </Button>
      ) : (
        <>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onApprovedAsNoted}
            disabled={pending}
          >
            Approve as Noted
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={onApprove}
            disabled={pending}
            className="bg-emerald-600 text-white hover:bg-emerald-700"
          >
            Approve
          </Button>
        </>
      )}
    </div>
  );
}

/** Friendly mapping for decide errors — never show raw error codes. */
function DecideError({ error }: { error: unknown }) {
  if (error instanceof ApiError && /auto.accept|baseline/i.test(error.message)) {
    return (
      <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
        Baseline-forming items can’t be approved with one click — firm policy requires your notes.
        Use <span className="font-semibold">Approve as Noted</span> instead.
      </div>
    );
  }
  return <ErrorMessage error={error} />;
}

/**
 * The single note step that replaces the verb buttons in place. One
 * sentence + (for approvals) a reason picked from the server's override
 * taxonomy — defaulted so the minimal flow is: type note, press Enter.
 * The proposed-value JSON is editable only behind a disclosure; unchanged
 * approvals submit the original value, which the API accepts.
 */
type NoteStep = 'approved_as_noted' | 'rejected' | 'revise';

function DecisionNoteBar({
  mode,
  proposalType,
  proposedValue,
  pending,
  onSubmit,
  onCancel,
  error,
}: {
  mode: NoteStep;
  proposalType: string;
  proposedValue: unknown;
  pending: boolean;
  onSubmit: (input: { reasonTag: string; note: string; editedValue: unknown }) => void;
  onCancel: () => void;
  error: ApiError | null;
}) {
  const isApprove = mode === 'approved_as_noted';
  const isRevise = mode === 'revise';
  const isEdl = proposalType === 'edl_item';
  const [reasonTag, setReasonTag] = useState('other');
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState(false);
  // "Lands in" — the Contract-baseline bucket this item commits into.
  // Defaults to the AI's classification; the consultant's pick wins.
  const [bucket, setBucket] = useState<string>(() => {
    const t = asRecord(proposedValue).item_type;
    return typeof t === 'string' && t in EDL_BUCKET_LABELS ? t : 'deliverable';
  });
  const [editedJson, setEditedJson] = useState<string>(() =>
    JSON.stringify(proposedValue, null, 2),
  );
  // C2-36: why the edited value cannot be submitted (invalid JSON, a domain
  // row without a name); shown inline under the editor.
  const [valueError, setValueError] = useState<string | null>(null);

  const noteOk = note.trim().length >= 10;

  function submit() {
    if (pending) return;
    if (!noteOk) {
      // Never die silently — this exact silence cost a user two attempts.
      setNoteError(true);
      return;
    }
    setNoteError(false);
    let value: unknown = proposedValue;
    if (isApprove) {
      const checked = validateEditedValue(proposalType, editedJson);
      if ('error' in checked) {
        setValueError(checked.error);
        return;
      }
      setValueError(null);
      value = checked.value;
      if (isEdl) {
        // The picker wins over whatever item_type is in the JSON.
        value = { ...(value as Record<string, unknown>), item_type: bucket };
      }
    }
    onSubmit({ reasonTag, note: note.trim(), editedValue: value });
  }

  return (
    <div
      className={cn(
        'rounded-md border p-3 text-sm',
        isApprove
          ? 'border-emerald-300 bg-emerald-50/50'
          : isRevise
            ? 'border-amber-300 bg-amber-50/50'
            : 'border-red-300 bg-red-50/50',
      )}
    >
      <div className="font-semibold">
        {isApprove
          ? 'Approve — add one note'
          : isRevise
            ? 'Revise & Resubmit — say what to fix'
            : 'Reject — say why'}
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground">
        {isApprove
          ? 'One sentence for the audit trail: why does this stand as approved?'
          : isRevise
            ? 'One sentence for the AI: what is wrong or missing? It re-reads the source with your note.'
            : 'One sentence for the audit trail: why is this wrong or unwanted?'}
      </p>
      <textarea
        // eslint-disable-next-line jsx-a11y/no-autofocus -- the note is the
        // sole purpose of this step; focusing it saves a click.
        autoFocus
        value={note}
        onChange={(e) => {
          setNote(e.target.value);
          if (e.target.value.trim().length >= 10) setNoteError(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        rows={2}
        className="mt-2 w-full rounded-md border bg-background p-2 text-sm"
        placeholder={
          isApprove
            ? 'e.g. Matches the deliverables in the signed change order.'
            : isRevise
              ? 'e.g. The due date is in section 4.2, not on the cover page.'
              : 'e.g. Not part of the contracted scope — no such deliverable in the SOW.'
        }
        aria-label={isApprove ? 'Approval note' : isRevise ? 'Revision note' : 'Rejection reason'}
      />
      {isApprove ? (
        <>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            {isEdl ? (
              <>
                <Label htmlFor="lands_in" className="text-muted-foreground">
                  Lands in
                </Label>
                <select
                  id="lands_in"
                  value={bucket}
                  onChange={(e) => setBucket(e.target.value)}
                  className="rounded-md border bg-background px-2 py-1 text-xs font-medium"
                >
                  {Object.entries(EDL_BUCKET_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </>
            ) : null}
            <ReasonTagSelect id="reason_tag" value={reasonTag} onChange={setReasonTag} />
          </div>
          {/* C2-36: a scoring model edits as domain rows; the JSON sits behind
            "Advanced" for every type, and invalid JSON says so inline. */}
          <ProposedValueEditor
            className="mt-3"
            proposalType={proposalType}
            json={editedJson}
            onJsonChange={setEditedJson}
            error={valueError}
            onErrorChange={setValueError}
          />
        </>
      ) : null}
      {error ? <div className="mt-2 text-xs text-destructive">{error.message}</div> : null}
      <div className="mt-3 flex items-center justify-between gap-2">
        <span className="text-[11px] text-muted-foreground">
          {noteError ? (
            <span className="font-semibold text-destructive">
              Your note needs at least 10 characters — one short sentence.
            </span>
          ) : noteOk ? (
            'Enter to confirm'
          ) : (
            'A few more words needed'
          )}
        </span>
        <div className="flex items-center gap-2">
          {pending ? <Spinner /> : null}
          <Button type="button" variant="ghost" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={isApprove || isRevise ? 'default' : 'destructive'}
            className={
              isApprove
                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                : isRevise
                  ? 'bg-amber-600 text-white hover:bg-amber-700'
                  : undefined
            }
            onClick={submit}
            disabled={pending}
          >
            {isApprove
              ? 'Confirm approval'
              : isRevise
                ? 'Send back for revision'
                : 'Confirm reject'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Small shared bits
// ===========================================================================

function DecisionBadge({
  decision,
  autoApproved,
}: {
  decision: 'pending' | 'approved' | 'approved_as_noted' | 'revise' | 'rejected';
  /** The system approved it under the firm auto-approve policy. */
  autoApproved?: boolean;
}) {
  if (decision === 'approved' && autoApproved) {
    return <Badge variant="secondary">Auto-approved</Badge>;
  }
  if (decision === 'approved') return <Badge variant="success">Approved</Badge>;
  if (decision === 'approved_as_noted') return <Badge variant="warning">Approved as Noted</Badge>;
  if (decision === 'revise') return <Badge variant="secondary">Revise</Badge>;
  if (decision === 'rejected') return <Badge variant="destructive">Rejected</Badge>;
  return <Badge variant="outline">Pending</Badge>;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}
